import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { Tabs } from '@/components/ui/Tabs';
import { useSession } from '@/features/session/useSession';
import { hasPermission } from '@/lib/permissions';
import { ExactOnlineTab } from './ExactOnlineTab';
import { CustomerIoTab } from './CustomerIoTab';
import { TicketsTab } from './TicketsTab';
import { LoyaltyTab } from './LoyaltyTab';
import { VenueTab } from './VenueTab';

const CONFIG_TABS = [
  { id: 'exact', label: 'Exact Online', permission: 'exact:configure' },
  { id: 'customerio', label: 'Customer.io', permission: 'customerio:configure' },
  { id: 'tickets', label: 'Tickets', permission: 'ticket:configure' },
  { id: 'loyalty', label: 'Loyalty', permission: 'loyalty:read' },
  { id: 'venue', label: 'Venue', permission: 'scope:manage' },
] as const;

function tabContent(id: (typeof CONFIG_TABS)[number]['id']) {
  switch (id) {
    case 'exact':
      return <ExactOnlineTab />;
    case 'customerio':
      return <CustomerIoTab />;
    case 'tickets':
      return <TicketsTab />;
    case 'loyalty':
      return <LoyaltyTab />;
    case 'venue':
      return <VenueTab />;
  }
}

/**
 * Configuration hub. Tabs are permission-filtered so a user with any one of
 * exact:configure / customerio:configure / ticket:configure / loyalty:read /
 * scope:manage can open the page and only sees the tabs they may use.
 */
export function ConfigurationPage() {
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const visible = CONFIG_TABS.filter((t) => hasPermission(me?.permissions, t.permission));
  const requested = params.get('tab');
  const active =
    visible.find((t) => t.id === requested)?.id ?? visible[0]?.id ?? 'exact';

  const items = useMemo(
    () =>
      visible.map((t) => ({
        id: t.id,
        label: t.label,
        content: tabContent(t.id),
      })),
    [visible],
  );

  if (visible.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        You do not have permission to view any configuration tabs.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Configuration
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Scope-level integrations and settings. Exact mappings are per venue;
          Customer.io templates and the loyalty earning pair are global.
        </p>
      </div>
      <Tabs
        items={items}
        value={active}
        onChange={(id) => {
          const next = new URLSearchParams(params);
          next.set('tab', id);
          setParams(next, { replace: true });
        }}
      />
    </div>
  );
}
